/**
 * CSV bulk import — 3-step wizard.
 *
 * Step 1 (Upload):  drag-drop + template download
 * Step 2 (Preview): server-side dry-run results, errors/warnings/preview table
 * Step 3 (Result):  counts, departments created, links to People page
 *
 * The server-side validation ride-share (``validateEmployeesCsv``) is a
 * SAVEPOINT-wrapped dry-run of the real import, so the preview we show is
 * exactly what the confirmation step will produce — no drift between
 * "what you saw" and "what got written".
 */
import { useRef, useState } from "react";
import { motion } from "framer-motion";
import {
  Upload,
  CheckCircle2,
  AlertTriangle,
  FileText,
  XCircle,
  ArrowLeft,
  Send,
} from "lucide-react";
import { inviteAllUninvited } from "@/api/invitations";
import { pageTransition } from "@/lib/utils";
import { PageHeader } from "@/components/layout/PageHeader";
import { Card } from "@/components/ui/Card";
import { Button } from "@/components/ui/Button";
import { Badge } from "@/components/ui/Badge";
import {
  validateEmployeesCsv,
  confirmEmployeesCsv,
  employeeImportTemplateUrl,
  type CsvImportReport,
} from "@/api/employees";
import { useToast } from "@/hooks/useToast";
import { useNavigate } from "react-router-dom";

type Step = "upload" | "preview" | "result";

export function CsvImportPage() {
  const navigate = useNavigate();
  const { addToast } = useToast();
  const fileInputRef = useRef<HTMLInputElement>(null);

  const [step, setStep] = useState<Step>("upload");
  const [file, setFile] = useState<File | null>(null);
  const [dragging, setDragging] = useState(false);
  const [createAccounts, setCreateAccounts] = useState(true);
  // Default for rows without an explicit ``employee_type`` column.
  // "existing" matches the spec — a 200-row migration shouldn't email
  // everyone unless the operator explicitly says these are new hires.
  const [defaultEmployeeType, setDefaultEmployeeType] = useState<
    "new_hire" | "existing"
  >("existing");
  const [busy, setBusy] = useState(false);
  const [report, setReport] = useState<CsvImportReport | null>(null);

  const reset = () => {
    setStep("upload");
    setFile(null);
    setReport(null);
  };

  const onFile = (f: File) => {
    if (!f.name.toLowerCase().endsWith(".csv")) {
      addToast("error", "Please select a .csv file");
      return;
    }
    setFile(f);
    setReport(null);
  };

  // FastAPI errors come in two shapes: a string ``detail`` (our
  // ValueError → HTTPException path) or a list of validation objects
  // (Pydantic 422). Stringify defensively so the toast can't render
  // ``[object Object]`` and the page never crashes on
  // ``ax.response.data.detail.toString()``.
  const errToString = (err: unknown, fallback: string): string => {
    const ax = err as {
      response?: { data?: { detail?: unknown }; status?: number };
      message?: string;
    };
    const detail = ax?.response?.data?.detail;
    if (typeof detail === "string" && detail.trim()) return detail;
    if (Array.isArray(detail) && detail.length > 0) {
      const first = detail[0] as { msg?: string; loc?: unknown[] };
      if (first?.msg) {
        const where = Array.isArray(first.loc) ? first.loc.join(".") : "";
        return where ? `${first.msg} (${where})` : first.msg;
      }
    }
    if (typeof ax?.message === "string" && ax.message) return ax.message;
    return fallback;
  };

  const runValidate = async () => {
    if (!file) return;
    setBusy(true);
    try {
      const r = await validateEmployeesCsv(
        file,
        createAccounts,
        defaultEmployeeType,
      );
      setReport(r);
      setStep("preview");
    } catch (err: unknown) {
      console.warn("CSV validate failed", err);
      addToast("error", errToString(err, "Validation failed"));
    } finally {
      setBusy(false);
    }
  };

  const runConfirm = async () => {
    if (!file) return;
    setBusy(true);
    try {
      const r = await confirmEmployeesCsv(
        file,
        createAccounts,
        defaultEmployeeType,
      );
      setReport(r);
      setStep("result");
      addToast("success", `Imported ${r.imported ?? 0} employees`);
    } catch (err: unknown) {
      console.warn("CSV confirm failed", err);
      addToast("error", errToString(err, "Import failed"));
    } finally {
      setBusy(false);
    }
  };

  return (
    <motion.div {...pageTransition}>
      <PageHeader
        title="Import Employees"
        subtitle="Bulk-create employees from a CSV file"
      />

      <StepBar step={step} />

      {step === "upload" && (
        <UploadStep
          file={file}
          dragging={dragging}
          createAccounts={createAccounts}
          defaultEmployeeType={defaultEmployeeType}
          busy={busy}
          onDragEnter={() => setDragging(true)}
          onDragLeave={() => setDragging(false)}
          onPickFile={() => fileInputRef.current?.click()}
          onDrop={(f) => {
            setDragging(false);
            onFile(f);
          }}
          onClear={() => setFile(null)}
          onToggleAccounts={setCreateAccounts}
          onChangeDefaultType={setDefaultEmployeeType}
          onValidate={runValidate}
          inputRef={fileInputRef}
          onFileInput={(e) => {
            const f = e.target.files?.[0];
            if (f) onFile(f);
          }}
        />
      )}

      {step === "preview" && report && file && (
        <PreviewStep
          file={file}
          report={report}
          busy={busy}
          onBack={() => setStep("upload")}
          onConfirm={runConfirm}
        />
      )}

      {step === "result" && report && (
        <ResultStep
          report={report}
          onAnother={reset}
          onViewEmployees={() => navigate("/employees")}
          onInvitesSent={(n) =>
            addToast(
              n > 0 ? "success" : "info",
              n > 0
                ? `Sent ${n} invitation${n === 1 ? "" : "s"}`
                : "No uninvited employees to send to"
            )
          }
        />
      )}
    </motion.div>
  );
}

// ─────────── Sub-components ───────────

function StepBar({ step }: { step: Step }) {
  const steps: { id: Step; label: string }[] = [
    { id: "upload", label: "Upload" },
    { id: "preview", label: "Preview & Validate" },
    { id: "result", label: "Results" },
  ];
  const activeIdx = steps.findIndex((s) => s.id === step);
  return (
    <div className="flex items-center gap-2 mb-6">
      {steps.map((s, i) => {
        const done = i < activeIdx;
        const active = i === activeIdx;
        return (
          <div key={s.id} className="flex items-center gap-2">
            <div
              className={`h-7 w-7 rounded-full flex items-center justify-center text-xs font-semibold ${
                active
                  ? "bg-accent text-white"
                  : done
                  ? "bg-success text-white"
                  : "bg-surface-hover text-text-muted"
              }`}
            >
              {done ? <CheckCircle2 className="h-4 w-4" /> : i + 1}
            </div>
            <span
              className={`text-sm ${
                active ? "font-medium text-text-primary" : "text-text-muted"
              }`}
            >
              {s.label}
            </span>
            {i < steps.length - 1 && (
              <div className="h-px w-8 bg-border mx-1" />
            )}
          </div>
        );
      })}
    </div>
  );
}

function UploadStep(props: {
  file: File | null;
  dragging: boolean;
  createAccounts: boolean;
  defaultEmployeeType: "new_hire" | "existing";
  busy: boolean;
  onDragEnter: () => void;
  onDragLeave: () => void;
  onPickFile: () => void;
  onDrop: (f: File) => void;
  onClear: () => void;
  onToggleAccounts: (v: boolean) => void;
  onChangeDefaultType: (v: "new_hire" | "existing") => void;
  onValidate: () => void;
  inputRef: React.RefObject<HTMLInputElement>;
  onFileInput: (e: React.ChangeEvent<HTMLInputElement>) => void;
}) {
  const {
    file,
    dragging,
    createAccounts,
    defaultEmployeeType,
    busy,
    onDragEnter,
    onDragLeave,
    onPickFile,
    onDrop,
    onClear,
    onToggleAccounts,
    onChangeDefaultType,
    onValidate,
    inputRef,
    onFileInput,
  } = props;

  return (
    <>
      <Card className="mb-6">
        <p className="text-xs font-medium uppercase tracking-wide text-text-muted mb-2">
          These employees are&hellip;
        </p>
        <div className="grid grid-cols-1 gap-2 sm:grid-cols-2">
          <label
            className={`flex items-start gap-2 rounded-md border p-3 cursor-pointer transition-colors ${
              defaultEmployeeType === "new_hire"
                ? "border-accent bg-accent/5"
                : "border-border hover:bg-surface-hover"
            }`}
          >
            <input
              type="radio"
              name="csv_employee_type"
              className="mt-1 accent-accent"
              checked={defaultEmployeeType === "new_hire"}
              onChange={() => onChangeDefaultType("new_hire")}
            />
            <div>
              <div className="text-sm font-medium text-text-primary">
                New Hires
              </div>
              <div className="text-xs text-text-muted mt-0.5">
                Send welcome emails so each employee can set up their
                account. Onboarding stays parked at &ldquo;pending&rdquo;
                until you click <strong>Start Onboarding</strong> on each
                profile.
              </div>
            </div>
          </label>
          <label
            className={`flex items-start gap-2 rounded-md border p-3 cursor-pointer transition-colors ${
              defaultEmployeeType === "existing"
                ? "border-accent bg-accent/5"
                : "border-border hover:bg-surface-hover"
            }`}
          >
            <input
              type="radio"
              name="csv_employee_type"
              className="mt-1 accent-accent"
              checked={defaultEmployeeType === "existing"}
              onChange={() => onChangeDefaultType("existing")}
            />
            <div>
              <div className="text-sm font-medium text-text-primary">
                Existing Team (migration)
              </div>
              <div className="text-xs text-text-muted mt-0.5">
                Silent import. No emails, no Pilot. You can send
                invitations later from the result screen.
              </div>
            </div>
          </label>
        </div>
        <p className="text-[11px] text-text-muted mt-2">
          Per-row override available via the optional{" "}
          <code>employee_type</code> column (
          <code>new_hire</code> or <code>existing</code>).
        </p>
      </Card>

      <Card
        className={`mb-6 transition-colors ${
          dragging ? "border-accent bg-accent/5" : ""
        }`}
      >
        <input
          ref={inputRef}
          type="file"
          accept=".csv,text/csv"
          className="hidden"
          onChange={onFileInput}
        />
        <div
          onDragEnter={(e) => {
            e.preventDefault();
            onDragEnter();
          }}
          onDragOver={(e) => e.preventDefault()}
          onDragLeave={onDragLeave}
          onDrop={(e) => {
            e.preventDefault();
            const f = e.dataTransfer.files[0];
            if (f) onDrop(f);
          }}
          onClick={onPickFile}
          className="cursor-pointer rounded-lg border-2 border-dashed border-border hover:border-accent/50 py-16 flex flex-col items-center justify-center gap-3 transition-colors"
        >
          <Upload className="h-10 w-10 text-text-muted" />
          <div className="text-center">
            <p className="text-base font-medium text-text-primary">
              {file ? file.name : "Drop your CSV file here or click to browse"}
            </p>
            <p className="text-xs text-text-muted mt-1">
              Maximum 500 employees per import · UTF-8 encoded
            </p>
          </div>
        </div>

        <div className="mt-4 flex items-center justify-between">
          <a
            href={employeeImportTemplateUrl()}
            className="text-sm text-accent hover:underline inline-flex items-center gap-1"
          >
            <FileText className="h-4 w-4" />
            Download template
          </a>
          {file && (
            <button
              onClick={onClear}
              className="text-xs text-text-muted hover:text-text-primary"
            >
              Clear
            </button>
          )}
        </div>
      </Card>

      {file && (
        <Card className="mb-6">
          <label className="flex items-center gap-2 text-sm mb-4">
            <input
              type="checkbox"
              checked={createAccounts}
              onChange={(e) => onToggleAccounts(e.target.checked)}
              className="rounded border-border text-accent focus:ring-accent"
            />
            Also create login accounts (temporary password:{" "}
            <code className="text-xs">Welcome123!</code>)
          </label>
          <Button
            onClick={onValidate}
            isLoading={busy}
            className="w-full"
          >
            Upload &amp; Validate
          </Button>
        </Card>
      )}

      <Card className="bg-background/50">
        <div className="flex items-start gap-3">
          <FileText className="h-4 w-4 text-text-muted flex-shrink-0 mt-0.5" />
          <div className="text-xs text-text-muted space-y-1">
            <p>
              <strong className="text-text-primary">Required columns:</strong>{" "}
              <code>first_name</code>, <code>last_name</code>,{" "}
              <code>email</code>, <code>department</code>.
            </p>
            <p>
              <strong className="text-text-primary">Optional:</strong>{" "}
              <code>title</code>, <code>hire_date</code>,{" "}
              <code>employment_type</code>, <code>manager_email</code>,{" "}
              <code>phone</code>, <code>personal_email</code>,{" "}
              <code>work_location</code>, <code>office_location</code>,{" "}
              <code>date_of_birth</code> (or <code>dob</code>),{" "}
              <code>sex</code> (M/F).
            </p>
            <p>
              Dates accepted: YYYY-MM-DD, MM/DD/YYYY, DD/MM/YYYY. Employment
              types: full_time, part_time, contractor, intern.
            </p>
          </div>
        </div>
      </Card>
    </>
  );
}

function PreviewStep(props: {
  file: File;
  report: CsvImportReport;
  busy: boolean;
  onBack: () => void;
  onConfirm: () => void;
}) {
  const { file, report, busy, onBack, onConfirm } = props;
  // Defensive defaults — the backend always populates these fields,
  // but a partial response (e.g. an early-return ValueError that the
  // caller swallowed into a synthetic shape) would otherwise crash
  // ``length`` access here and trip the route error boundary
  // ("Something went wrong on this page"). Empty arrays render an
  // empty section rather than blowing up.
  const errors = report.errors ?? [];
  const warnings = report.warnings ?? [];
  const preview = report.preview ?? [];
  const departmentsCreated = report.departments_created ?? [];
  const validCount = report.valid_count ?? 0;
  const errorCount = report.error_count ?? errors.length;
  const warningsCount = report.warnings_count ?? warnings.length;
  const totalRows = report.total_rows ?? 0;
  const canImport = validCount > 0;
  return (
    <>
      <Card className="mb-4">
        <div className="flex items-center justify-between flex-wrap gap-3">
          <div>
            <p className="text-sm font-medium text-text-primary">
              {file.name}
            </p>
            <p className="text-xs text-text-muted">
              {totalRows} rows scanned
            </p>
          </div>
          <div className="flex items-center gap-2">
            <Badge variant="success">
              {validCount} valid
            </Badge>
            {errorCount > 0 && (
              <Badge variant="danger">{errorCount} errors</Badge>
            )}
            {warningsCount > 0 && (
              <Badge variant="warning">
                {warningsCount} warnings
              </Badge>
            )}
          </div>
        </div>
      </Card>

      {errors.length > 0 && (
        <IssuePanel
          tone="danger"
          icon={<XCircle className="h-4 w-4" />}
          title={`${errors.length} error${
            errors.length === 1 ? "" : "s"
          } — these rows will be skipped`}
          issues={errors}
        />
      )}

      {warnings.length > 0 && (
        <IssuePanel
          tone="warning"
          icon={<AlertTriangle className="h-4 w-4" />}
          title={`${warnings.length} warning${
            warnings.length === 1 ? "" : "s"
          }`}
          issues={warnings}
        />
      )}

      {preview.length > 0 && (
        <Card className="mb-4">
          <div className="mb-3">
            <p className="text-sm font-medium text-text-primary">
              Preview — first {preview.length} valid row
              {preview.length === 1 ? "" : "s"}
            </p>
          </div>
          <div className="overflow-x-auto">
            <table className="w-full text-xs">
              <thead className="text-text-muted border-b border-border/50">
                <tr>
                  <th className="text-left py-2 pr-3">Name</th>
                  <th className="text-left py-2 pr-3">Email</th>
                  <th className="text-left py-2 pr-3">Title</th>
                  <th className="text-left py-2 pr-3">Department</th>
                  <th className="text-left py-2 pr-3">Hire date</th>
                  <th className="text-left py-2">Type</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-border/30">
                {preview.map((p) => (
                  <tr key={p.row}>
                    <td className="py-2 pr-3 text-text-primary">
                      {p.first_name} {p.last_name}
                    </td>
                    <td className="py-2 pr-3 text-text-muted">{p.email}</td>
                    <td className="py-2 pr-3">{p.title}</td>
                    <td className="py-2 pr-3">{p.department}</td>
                    <td className="py-2 pr-3">{p.hire_date}</td>
                    <td className="py-2">{p.employment_type}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </Card>
      )}

      {departmentsCreated.length > 0 && (
        <Card className="mb-4 border-accent/30 bg-accent/5">
          <p className="text-sm">
            <strong>{departmentsCreated.length}</strong> new department
            {departmentsCreated.length === 1 ? "" : "s"} will be
            created:{" "}
            <span className="text-text-muted">
              {departmentsCreated.join(", ")}
            </span>
          </p>
        </Card>
      )}

      <div className="flex gap-2">
        <Button variant="secondary" onClick={onBack} disabled={busy}>
          <ArrowLeft className="h-4 w-4" /> Back
        </Button>
        <Button
          onClick={onConfirm}
          isLoading={busy}
          disabled={!canImport}
          className="flex-1"
        >
          Import {validCount} employee
          {validCount === 1 ? "" : "s"}
        </Button>
      </div>
    </>
  );
}

function ResultStep(props: {
  report: CsvImportReport;
  onAnother: () => void;
  onViewEmployees: () => void;
  onInvitesSent: (n: number) => void;
}) {
  const { report, onAnother, onViewEmployees, onInvitesSent } = props;
  const [inviting, setInviting] = useState(false);
  const [invitesSent, setInvitesSent] = useState<number | null>(null);

  // Same defensive defaults as PreviewStep — never crash on a
  // malformed response.
  const errors = report.errors ?? [];
  const departmentsCreated = report.departments_created ?? [];
  const imported = report.imported ?? 0;
  const skipped = report.skipped ?? 0;
  const errorCount = report.error_count ?? errors.length;

  const handleInvite = async () => {
    setInviting(true);
    try {
      const res = await inviteAllUninvited();
      setInvitesSent(res.sent);
      onInvitesSent(res.sent);
    } catch {
      onInvitesSent(0);
    } finally {
      setInviting(false);
    }
  };

  return (
    <>
      <Card className="mb-4">
        <div className="flex items-center gap-3">
          <div className="h-10 w-10 rounded-full bg-success/15 flex items-center justify-center">
            <CheckCircle2 className="h-6 w-6 text-success" />
          </div>
          <div>
            <p className="text-lg font-semibold text-text-primary">
              {imported} employee{imported === 1 ? "" : "s"}{" "}
              imported successfully
            </p>
            <p className="text-xs text-text-muted">
              {skipped} skipped (already exist) · {errorCount}{" "}
              errors
            </p>
          </div>
        </div>
      </Card>

      {departmentsCreated.length > 0 && (
        <Card className="mb-4 border-accent/30 bg-accent/5">
          <p className="text-sm">
            <strong>{departmentsCreated.length}</strong> new department
            {departmentsCreated.length === 1 ? "" : "s"} created:{" "}
            <span className="text-text-muted">
              {departmentsCreated.join(", ")}
            </span>
          </p>
        </Card>
      )}

      {errors.length > 0 && (
        <IssuePanel
          tone="danger"
          icon={<XCircle className="h-4 w-4" />}
          title={`${errors.length} row${
            errors.length === 1 ? "" : "s"
          } skipped due to errors`}
          issues={errors}
        />
      )}

      {imported > 0 && invitesSent === null && (
        <Card className="mb-4 border-accent/40 bg-accent/5">
          <div className="flex items-start gap-3">
            <div className="h-9 w-9 rounded-full bg-accent/15 flex items-center justify-center shrink-0">
              <Send className="h-4 w-4 text-accent" />
            </div>
            <div className="flex-1">
              <p className="text-sm font-medium text-text-primary">
                Send setup-password invitations?
              </p>
              <p className="text-xs text-text-muted mt-1">
                New employees need a link to set their password and activate
                their account. This sends invitations to every employee who
                hasn't been invited yet.
              </p>
              <div className="flex gap-2 mt-3">
                <Button size="sm" onClick={handleInvite} isLoading={inviting}>
                  <Send className="h-4 w-4" />
                  Send invitations
                </Button>
                <Button
                  size="sm"
                  variant="ghost"
                  onClick={() => setInvitesSent(0)}
                  disabled={inviting}
                >
                  Skip for now
                </Button>
              </div>
            </div>
          </div>
        </Card>
      )}

      {invitesSent !== null && invitesSent > 0 && (
        <Card className="mb-4 border-success/30 bg-success/5">
          <p className="text-sm text-text-primary">
            <CheckCircle2 className="inline h-4 w-4 text-success mr-1" />
            Sent {invitesSent} invitation{invitesSent === 1 ? "" : "s"}.
          </p>
        </Card>
      )}

      <div className="flex gap-2 mt-6">
        <Button onClick={onViewEmployees} className="flex-1">
          View imported employees
        </Button>
        <Button variant="secondary" onClick={onAnother}>
          Import another file
        </Button>
      </div>
    </>
  );
}

function IssuePanel(props: {
  tone: "danger" | "warning";
  icon: React.ReactNode;
  title: string;
  issues: { row: number; field: string; message: string }[];
}) {
  const { tone, icon, title, issues } = props;
  const bg =
    tone === "danger"
      ? "border-danger/30 bg-danger/5"
      : "border-warning/30 bg-warning/5";
  const fg = tone === "danger" ? "text-danger" : "text-warning";
  return (
    <Card className={`mb-4 ${bg}`}>
      <div className={`flex items-center gap-2 mb-2 font-medium text-sm ${fg}`}>
        {icon}
        {title}
      </div>
      <ul className="max-h-56 overflow-y-auto text-xs divide-y divide-border/30 border border-border/30 rounded bg-background">
        {issues.slice(0, 100).map((e, i) => (
          <li
            key={`${e.row}-${e.field}-${i}`}
            className="px-3 py-1.5 flex gap-3"
          >
            <span className="text-text-muted shrink-0">Row {e.row}</span>
            <span className="text-text-muted shrink-0 uppercase text-[10px] tracking-wide mt-0.5">
              {e.field}
            </span>
            <span className={fg}>{e.message}</span>
          </li>
        ))}
        {issues.length > 100 && (
          <li className="px-3 py-1.5 text-text-muted">
            …and {issues.length - 100} more
          </li>
        )}
      </ul>
    </Card>
  );
}
